/* ============================================================
   Laley PDP Clean v4 — product-ui
   Modulær PDP-runtime. Den gamle v3-monolitten er kun historisk arkiv.
   Registrerer metoder på LaleyPDP.prototype via PDP.registerModule.
   Krever pdp-clean-v4-core.js lastet først (defer-rekkefølge i product-clean-v3-1.liquid).
   ============================================================ */
(() => {
  const PDP = window.LaleyPDPCleanV4;
  if (!PDP || typeof PDP.registerModule !== 'function') {
    console.error('[PDP] Kjerne mangler — hopper over modul: product-ui');
    return;
  }
  const { cssEscape, lockRootScroll, unlockRootScroll } = PDP.utils;

  PDP.registerModule('product-ui', {
    bindTabs() {
      // Desktop: horisontale faner. Mobil (≤989px): LUKKEDE accordions
      // (SKIMS/COS-mønsteret) — hvert panel flyttes inn rett etter sin knapp,
      // alt lukket som default, instant toggle uten animasjon.
      const root = this.section.querySelector('[data-pdp-tabs]') || this.section.querySelector('[data-pdp-accordions]');
      if (!root) return;
      const nav = root.querySelector('.pdp-clean__tabs-nav');
      const tabs = Array.from(root.querySelectorAll('[data-pdp-tab]'));
      const panels = Array.from(root.querySelectorAll('[data-pdp-panel]'));
      if (!tabs.length || !nav) return;
      const panelFor = (key) => panels.find((p) => p.dataset.pdpPanel === key);
      const mq = window.matchMedia('(max-width: 989px)');
      let accordion = false;

      const activate = (key, opts) => {
        const focus = opts && opts.focus;
        tabs.forEach((t) => {
          const on = t.dataset.pdpTab === key;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
          if (on && focus) t.focus();
        });
        panels.forEach((p) => {
          const on = p.dataset.pdpPanel === key;
          p.classList.toggle('is-active', on);
          p.setAttribute('aria-hidden', on ? 'false' : 'true');
          p.hidden = !on;
        });
      };

      const setOpen = (tab, open) => {
        const panel = panelFor(tab.dataset.pdpTab);
        tab.classList.toggle('is-open', open);
        tab.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (panel) {
          panel.classList.toggle('is-active', open);
          panel.setAttribute('aria-hidden', open ? 'false' : 'true');
          panel.hidden = !open;
        }
      };

      const enterAccordion = () => {
        accordion = true;
        root.classList.add('pdp-clean__tabs--accordion');
        nav.removeAttribute('role');
        tabs.forEach((tab) => {
          const panel = panelFor(tab.dataset.pdpTab);
          if (panel && panel.previousElementSibling !== tab) tab.insertAdjacentElement('afterend', panel);
          tab.removeAttribute('role');
          tab.removeAttribute('aria-selected');
          tab.classList.remove('is-active');
          tab.tabIndex = 0;
          setOpen(tab, false);
        });
      };

      const enterTabs = () => {
        accordion = false;
        root.classList.remove('pdp-clean__tabs--accordion');
        nav.setAttribute('role', 'tablist');
        panels.forEach((p) => root.appendChild(p));
        tabs.forEach((tab) => {
          tab.setAttribute('role', 'tab');
          tab.removeAttribute('aria-expanded');
          tab.classList.remove('is-open');
        });
        activate(tabs[0].dataset.pdpTab);
      };

      tabs.forEach((tab) => {
        this.on(tab, 'click', () => {
          if (accordion) setOpen(tab, !tab.classList.contains('is-open'));
          else activate(tab.dataset.pdpTab);
        });
        // WAI-ARIA tabs: pil venstre/høyre bytter fane (kun fane-modus)
        this.on(tab, 'keydown', (e) => {
          if (accordion) return;
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          e.preventDefault();
          const i = tabs.indexOf(tab);
          const dir = e.key === 'ArrowRight' ? 1 : -1;
          const next = tabs[(i + dir + tabs.length) % tabs.length];
          activate(next.dataset.pdpTab, { focus: true });
        });
      });

      if (mq.matches) enterAccordion();
      this.on(mq, 'change', (e) => { if (e.matches) enterAccordion(); else enterTabs(); });

      // Hook for review proof links/cards. Reviews may be a lower standalone
      // section now; only activate the tab when that tab actually exists.
      const reviewTab = tabs.find((tab) => tab.dataset.pdpTab === 'anmeldelser');
      this.activateReviewsTab = () => {
        if (!reviewTab) return;
        if (accordion) setOpen(reviewTab, true);
        else activate('anmeldelser');
      };
    },

    // placeOutfitModule (kompakt inline-variant i kjøpsflyten) er pensjonert
    // juli-26: Fullfør antrekket er tilbake som redaksjonell rail under
    // produktet (Sondres beslutning). Kompaktkort-formen lever videre i
    // handlekurvens Fullfør antrekket-flow (cart-drawer.liquid).

    bindLagetForDeg() {
      // Re-baseringen (ARKITEKTUR.md #1): henter anbefalings-railen — vår
      // snippet server-rendret av Section Rendering API — inn i verten.
      // Klaviyo-hearts plukkes opp av deres egen observer.
      const host = document.querySelector('[data-pdp-lfd]');
      const url = host?.dataset.pdpLfdUrl;
      if (!host || !url) return;
      fetch(url)
        .then((res) => (res.ok ? res.text() : ''))
        .then((html) => {
          if (!html) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const rail = doc.querySelector('[data-pdp-similar]');
          if (rail) host.replaceWith(rail);
          else host.remove();
        })
        .catch(() => {});
    },

    bindDetailsClamp() {
      // Detaljer: når introen kuttes med ellipse, er selve tekstflaten en
      // rolig expand-affordance med tastaturstøtte.
      const clamp = this.section.querySelector('[data-pdp-details-clamp]');
      if (!clamp) return;

      const visibleIntros = () => Array.from(clamp.querySelectorAll('.pdp-clean__details-intro'))
        .filter((node) => {
          const style = window.getComputedStyle(node);
          return style.display !== 'none' && style.visibility !== 'hidden' && node.getBoundingClientRect().width > 0;
        });

      const measureOverflow = () => {
        if (clamp.closest('[hidden], [aria-hidden="true"]')) return false;
        return visibleIntros().some((node) => (
          node.scrollHeight > node.clientHeight + 2
        ));
      };

      const setExpanded = (expanded) => {
        if (!clamp.classList.contains('is-collapsible')) return;
        clamp.classList.toggle('is-expanded', expanded);
        clamp.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      };

      const sync = () => {
        const hasOverflow = clamp.classList.contains('is-expanded') || measureOverflow();
        clamp.classList.toggle('is-collapsible', hasOverflow);
        if (hasOverflow) {
          clamp.setAttribute('role', 'button');
          clamp.tabIndex = 0;
          clamp.setAttribute('aria-expanded', clamp.classList.contains('is-expanded') ? 'true' : 'false');
        } else {
          clamp.removeAttribute('role');
          clamp.removeAttribute('tabindex');
          clamp.removeAttribute('aria-expanded');
        }
      };
      this.on(clamp, 'click', (event) => {
        if (event.target.closest('a, button')) return;
        if (!clamp.classList.contains('is-collapsible')) return;
        setExpanded(!clamp.classList.contains('is-expanded'));
        sync();
      });
      this.on(clamp, 'keydown', (event) => {
        if (!clamp.classList.contains('is-collapsible')) return;
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        setExpanded(!clamp.classList.contains('is-expanded'));
        sync();
      });
      sync();
      // Webfonter/lazy layout endrer linjetall etter init — mål igjen når
      // fontene er klare og etter korte intervaller.
      if (document.fonts?.ready) document.fonts.ready.then(() => sync()).catch(() => {});
      [600, 1800].forEach((delay) => this.timers.push(window.setTimeout(sync, delay)));
      let clampRaf = null;
      this.on(window, 'resize', () => {
        if (clampRaf) return;
        clampRaf = window.requestAnimationFrame(() => { clampRaf = null; sync(); });
      }, { passive: true });
      // Tab-panelet kan være skjult ved init (accordion på mobil) — mål igjen
      // når Detaljer-fanen aktiveres.
      const panel = clamp.closest('[data-pdp-panel]');
      if (panel) {
        const obs = new MutationObserver(sync);
        obs.observe(panel, { attributes: true, attributeFilter: ['hidden', 'aria-hidden', 'class'] });
        this.unsubs.push(() => obs.disconnect());
      }
    },

    bindSizeGuide() {
      const modal = this.section.querySelector('[data-pdp-sizeguide]');
      if (!modal) return;
      let scrollLockToken = null;
      const open = () => {
        modal.hidden = false;
        scrollLockToken = lockRootScroll(scrollLockToken || Symbol('pdp-sizeguide-lock'));
        const c = modal.querySelector('.pdp-clean__sizeguide-close');
        if (c) window.requestAnimationFrame(() => c.focus());
      };
      const close = () => {
        modal.hidden = true;
        unlockRootScroll(scrollLockToken);
        scrollLockToken = null;
      };
      this.section.querySelectorAll('[data-pdp-sizeguide-open]').forEach((btn) => {
        this.on(btn, 'click', (e) => { e.preventDefault(); open(); });
      });
      modal.querySelectorAll('[data-pdp-sizeguide-close]').forEach((el) => {
        this.on(el, 'click', close);
      });
      this.on(document, 'keydown', (e) => { if (e.key === 'Escape' && !modal.hidden) close(); });
      this.unsubs.push(() => {
        unlockRootScroll(scrollLockToken);
        scrollLockToken = null;
      });
    },

    bindModelInfoOverlay() {
      const buttons = Array.from(this.section.querySelectorAll('[data-pdp-model-toggle]'));
      if (!buttons.length) return;

      const pairs = buttons.map((button) => {
        const item = button.closest('.pdp-clean__gallery-item') || button.closest('[data-pdp-gallery-wrap]');
        const id = button.getAttribute('aria-controls');
        const overlay = id
          ? this.section.querySelector(`#${cssEscape(id)}`)
          : item?.querySelector('[data-pdp-model-overlay]');

        return (item && overlay) ? { button, item, overlay } : null;
      }).filter(Boolean);

      if (!pairs.length) return;

      const setOpen = ({ button, item, overlay }, open) => {
        overlay.hidden = !open;
        item.classList.toggle('is-model-open', open);
        button.setAttribute('aria-expanded', open ? 'true' : 'false');
      };

      const closeAll = (except = null) => {
        pairs.forEach((pair) => {
          if (pair !== except) setOpen(pair, false);
        });
      };

      pairs.forEach((pair) => {
        this.on(pair.button, 'click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const shouldOpen = pair.overlay.hidden;
          closeAll(pair);
          setOpen(pair, shouldOpen);
        });

        this.on(pair.button, 'keydown', (event) => {
          event.stopPropagation();
        });

        // Teksten ER chipen: klikk på den lukker.
        this.on(pair.overlay, 'click', () => setOpen(pair, false));
      });

      this.on(document, 'keydown', (event) => {
        if (event.key === 'Escape') closeAll();
      });
    },
  });
})();
